import React, { ReactNode, useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import Header from "../Header";
import {
  HomeIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UsersIcon,
  UserMinusIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMsal } from "@azure/msal-react";

interface LayoutProps {
  children: ReactNode;
}

interface NavItemProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  isMobile: boolean;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon: Icon,
  label,
  isActive,
  isCollapsed,
  isMobile,
  onClick,
}) => (
  <li>
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center p-2 text-base font-normal rounded-lg transition-colors group ${isActive
        ? "bg-gray-100 text-gray-900"
        : "text-gray-900 hover:bg-gray-100"
        }`}
    >
      <Icon className="w-6 h-6 flex-shrink-0" />
      {!isCollapsed && !isMobile && (
        <span className="ml-3">{label}</span>
      )}
      {(isCollapsed || isMobile) && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          {label}
        </div>
      )}
    </Link>
  </li>
);

interface SubNavItemProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isActive: boolean;
  onClick?: () => void;
}

const SubNavItem: React.FC<SubNavItemProps> = ({ to, icon: Icon, label, isActive, onClick }) => (
  <li className="ml-6">
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center py-2 px-3 text-sm font-normal rounded-lg transition-colors ${isActive
        ? "bg-gray-100 text-gray-900"
        : "text-gray-700 hover:bg-gray-100"
        }`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="ml-2">{label}</span>
    </Link>
  </li>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isAdminExpanded, setIsAdminExpanded] = useState(false);
  const [showMobileAdminMenu, setShowMobileAdminMenu] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isReviewMode = searchParams.get("mode") === "review";
  const originPath = location.state?.from as string | undefined;

  const isActive = useCallback((path: string) => {
    if (isReviewMode) return path === "/review-items";
    if (originPath) return path === originPath;
    return location.pathname === path;
  }, [location.pathname, isReviewMode, originPath]);

  const { accounts } = useMsal();
  const [selectedRole, setSelectedRole] = useState<string>(
    () => localStorage.getItem("selectedRole") || ""
  );
  const isStandaloneHelpPage = location.pathname === "/help";
  const canViewMySubmissions = ["DCCM", "DM", "VP"].includes(selectedRole);

  // Memoize user roles and permissions
  const { userRoles, hasPayrollRole, hasOpsRole, hasAdminAccess } = useMemo(() => {

    const roleMap: Record<string, string> = {
      "DCCM-Review": "DCCM",
      "SPM-Review": "SPM",
      "VP-Review": "VP",
      "DM-Review": "DM",
    };

    const roles = [
      ...new Set(
        ((accounts[0]?.idTokenClaims?.roles as string[]) || []).map(
          (role) => roleMap[role] || role
        )
      ),
    ].sort();

    return {
      userRoles: roles,
      hasPayrollRole: roles.includes("Payroll"),
      hasOpsRole: roles.includes("Ops"),
      hasAdminRole: roles.includes("Admin"),
      hasAdminAccess: ["Admin", "Ops", "Payroll"].some(role => roles.includes(role)),
    };
  }, [accounts]);

  // Handle responsive behavior
  useEffect(() => {
    const handleRoleChange = (e: CustomEvent<string>) => {
      setSelectedRole(e.detail);
    };
    window.addEventListener("roleChanged", handleRoleChange as EventListener);
    return () => window.removeEventListener("roleChanged", handleRoleChange as EventListener);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // mobile
        setIsCollapsed(true);
        setIsMobile(true);
      } else if (width >= 768 && width < 1024) {
        // tablet
        setIsMobile(false);
        setIsCollapsed(true);
      } else {
        // desktop
        setIsMobile(false);
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-expand Admin menu when on admin pages
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      setIsAdminExpanded(true);
    }
  }, [location.pathname]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const toggleAdminExpanded = useCallback(() => {
    setIsAdminExpanded(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    if (isMobile) {
      setShowMobileAdminMenu(false);
    }
  }, [isMobile]);

  const handleMobileAdminClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowMobileAdminMenu(prev => !prev);
  }, []);

  const NavigationItems = useMemo(() => (
    <ul className="space-y-2 font-medium">
      {(['CCM1', 'CCM2', 'CCM3', 'DCCM', 'CM3', 'PM', 'SPM'] as const).includes(selectedRole as any) && (
        <NavItem
          to="/my-applications"
          icon={HomeIcon}
          label="My Applications"
          isActive={isActive("/my-applications")}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          onClick={closeMobileMenu}
        />
      )}
      {canViewMySubmissions && (
        <NavItem
          to="/my-submissions"
          icon={ClipboardDocumentListIcon}
          label="My Submissions"
          isActive={isActive("/my-submissions")}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          onClick={closeMobileMenu}
        />
      )}
      {selectedRole !== "Payroll" && (
        <NavItem
          to="/review-items"
          icon={ClipboardDocumentCheckIcon}
          label="Review"
          isActive={isActive("/review-items")}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          onClick={closeMobileMenu}
        />
      )}
      <NavItem
        to="/history"
        icon={ClipboardDocumentListIcon}
        label="History"
        isActive={isActive("/history")}
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        onClick={closeMobileMenu}
      />

      {hasPayrollRole && selectedRole === "Payroll" && (
        <NavItem
          to="/payroll"
          icon={BanknotesIcon}
          label="Payroll"
          isActive={isActive("/payroll")}
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          onClick={closeMobileMenu}
        />
      )}

      {hasAdminAccess && (selectedRole === "Ops" || selectedRole === "Payroll") && (
        <li>
          <button
            onClick={toggleAdminExpanded}
            className={`w-full flex items-center justify-between p-2 text-base font-normal rounded-lg transition-colors group ${location.pathname.startsWith("/admin") || location.pathname === "/payroll/files"
              ? "bg-gray-100 text-gray-900"
              : "text-gray-900 hover:bg-gray-100"
              }`}
          >
            <div className="flex items-center">
              <Squares2X2Icon className="w-6 h-6 flex-shrink-0" />
              {!isCollapsed && !isMobile && <span className="ml-3">Admin</span>}
            </div>
            {!isCollapsed && !isMobile && (
              <span className="ml-auto">
                {isAdminExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </span>
            )}
            {(isCollapsed || isMobile) && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                Admin
              </div>
            )}
          </button>

          {isAdminExpanded && !isCollapsed && !isMobile && (
            <ul className="mt-2 space-y-1">
              <li className="ml-6">
                <div className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                  Manage
                </div>
              </li>
              {/* <SubNavItem
                to="/admin/manage/hierarchy-overrides"
                icon={UsersIcon}
                label="Hierarchy Overrides"
                isActive={isActive("/admin/manage/hierarchy-overrides")}
                onClick={closeMobileMenu}
              /> */}
              {hasOpsRole && selectedRole === "Ops" && (
                <SubNavItem
                  to="/admin/manage/applications"
                  icon={ClipboardDocumentListIcon}
                  label="Applications"
                  isActive={isActive("/admin/manage/applications")}
                  onClick={closeMobileMenu}
                />)}
              {((hasPayrollRole && selectedRole === "Payroll") ||
                (hasOpsRole && selectedRole === "Ops")) && (
                  <SubNavItem
                    to="/payroll/files"
                    icon={BanknotesIcon}
                    label="Payroll File Logs"
                    isActive={isActive("/payroll/files")}
                    onClick={closeMobileMenu}
                  />
                )}
            </ul>
          )}
        </li>
      )}
    </ul>
  ), [
    isActive,
    isCollapsed,
    isMobile,
    canViewMySubmissions,
    hasPayrollRole,
    hasOpsRole,
    selectedRole,
    hasAdminAccess,
    location.pathname,
    isAdminExpanded,
    closeMobileMenu,
    toggleAdminExpanded,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Desktop/Tablet Sidebar */}
      {!isStandaloneHelpPage && (
        <aside
          className={`fixed left-0 top-0 z-40 h-screen pt-16 transition-all duration-300 bg-white border-r border-gray-200 hidden md:block ${isCollapsed ? "w-[63px]" : "w-64"
            }`}
        >
          <div className="h-full px-3 pb-4 overflow-y-auto relative">
            <button
              onClick={toggleCollapse}
              className="absolute right-2 top-2 p-2 rounded-lg hover:bg-gray-100 transition-colors z-10"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRightIcon className="w-5 h-5" />
              ) : (
                <ChevronLeftIcon className="w-5 h-5" />
              )}
            </button>
            <div className="pt-12">{NavigationItems}</div>
          </div>
        </aside>
      )}

      {/* Mobile Bottom Navigation */}
      {!isStandaloneHelpPage && (
        <nav className="fixed bottom-0 left-0 z-50 w-full h-16 bg-white border-t border-gray-200 md:hidden">
          <div className={`grid h-full max-w-lg ${(canViewMySubmissions || (hasAdminAccess && (selectedRole === "Ops" || selectedRole === "Payroll"))) ? "grid-cols-4" : "grid-cols-3"} mx-auto font-medium`}>
            <Link
              to="/my-applications"
              className={`inline-flex flex-col items-center justify-center px-5 ${isActive("/my-applications")
                ? "text-blue-600"
                : "text-gray-500 hover:bg-gray-50"
                }`}
            >
              <HomeIcon className="w-6 h-6 mb-1" />
              <span className="text-xs">Home</span>
            </Link>
            <Link
              to="/review-items"
              className={`inline-flex flex-col items-center justify-center px-5 ${isActive("/review-items")
                ? "text-blue-600"
                : "text-gray-500 hover:bg-gray-50"
                }`}
            >
              <ClipboardDocumentCheckIcon className="w-6 h-6 mb-1" />
              <span className="text-xs">Review</span>
            </Link>
            <Link
              to="/history"
              className={`inline-flex flex-col items-center justify-center px-5 ${isActive("/history")
                ? "text-blue-600"
                : "text-gray-500 hover:bg-gray-50"
                }`}
            >
              <ClipboardDocumentListIcon className="w-6 h-6 mb-1" />
              <span className="text-xs">History</span>
            </Link>
            {canViewMySubmissions && (
              <Link
                to="/my-submissions"
                className={`inline-flex flex-col items-center justify-center px-5 ${isActive("/my-submissions")
                  ? "text-blue-600"
                  : "text-gray-500 hover:bg-gray-50"
                  }`}
              >
                <ClipboardDocumentListIcon className="w-6 h-6 mb-1" />
                <span className="text-xs">Submissions</span>
              </Link>
            )}
            {hasAdminAccess && (selectedRole === "Ops" || selectedRole === "Payroll") && (
              <button
                onClick={handleMobileAdminClick}
                className={`inline-flex flex-col items-center justify-center px-5 ${location.pathname.startsWith("/admin") || showMobileAdminMenu
                  ? "text-blue-600"
                  : "text-gray-500 hover:bg-gray-50"
                  }`}
              >
                <Squares2X2Icon className="w-6 h-6 mb-1" />
                <span className="text-xs">Admin</span>
              </button>
            )}
          </div>
        </nav>
      )}

      {/* Mobile Admin Menu Overlay */}
      {!isStandaloneHelpPage && showMobileAdminMenu && hasAdminAccess && (selectedRole === "Ops" || selectedRole === "Payroll") && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowMobileAdminMenu(false)}
          />

          {/* Menu */}
          <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden rounded-t-xl shadow-lg max-h-[70vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Admin</h3>
                <button
                  onClick={() => setShowMobileAdminMenu(false)}
                  className="text-gray-500 hover:text-gray-700 p-1"
                  aria-label="Close admin menu"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-1">
                <div className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                  Manage
                </div>

                {/* <Link
                  to="/admin/manage/hierarchy-overrides"
                  onClick={closeMobileMenu}
                  className={`flex items-center py-3 px-3 text-base font-normal rounded-lg transition-colors ${isActive("/admin/manage/hierarchy-overrides")
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  <UsersIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="ml-3">Hierarchy Overrides</span>
                </Link> */}

                {(hasOpsRole && selectedRole === "Ops") && (<Link
                  to="/admin/manage/applications"
                  onClick={closeMobileMenu}
                  className={`flex items-center py-3 px-3 text-base font-normal rounded-lg transition-colors ${isActive("/admin/manage/applications")
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  <ClipboardDocumentListIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="ml-3">Applications</span>
                </Link>)}

                {((hasPayrollRole && selectedRole === "Payroll") ||
                  (hasOpsRole && selectedRole === "Ops")) && (<Link
                    to="/payroll/files"
                    onClick={closeMobileMenu}
                    className={`flex items-center py-3 px-3 text-base font-normal rounded-lg transition-colors ${isActive("/payroll/files")
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    <BanknotesIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="ml-3">Payroll File Logs</span>
                  </Link>)}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main
        className={`pt-16 ${isStandaloneHelpPage ? "pb-4" : "pb-20 md:pb-4"} transition-all duration-300 ${isStandaloneHelpPage ? "md:pl-0" : (isCollapsed ? "md:pl-[63px]" : "md:pl-64")
          }`}
      >
        <div className="p-4">{children}</div>
      </main>
    </div>
  );
};

export default Layout;